/**
 * Every tracking URL, with what it earned and what can be done to it.
 *
 * Delete is Archive here, and the button says so. A tracking URL is printed on
 * posters and sitting in messages that have already gone out — removing the
 * row would break every one of them and take its click history with it, so the
 * action people reach for stops it redirecting and keeps the record.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.jsx';
import api from '../../lib/api.js';
import { useTracking, changed, download } from '../../lib/urltracking.js';
import { formatDate, parseDate } from '../../lib/dates.js';
import Icon from '../../lib/icons.jsx';
import { Card, CardHead, CardBody, EmptyState, Avatar, Dash, Pill } from '../../components/Primitives.jsx';
import { SOURCES, MEDIUMS, LINK_STATUS } from '../../constants/urltracking.js';
import { TrackStatus, SourcePill, Destination, ShortUrl, TrackState, PresetInput, num } from './TrackBits.jsx';

export default function TrackLinks() {
  const { S, setSheet, can, toast } = useApp();
  const navigate = useNavigate();

  const [f, setF] = useState({ q: '', campaignId: '', source: '', medium: '', status: '' });
  const [showArchived, setShowArchived] = useState(false);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const campaigns = useTracking(() => api.urlTracking.campaigns.list({ limit: 500 }));
  const { data, error, loading } = useTracking(
    () => api.urlTracking.links.list({ ...f, archived: showArchived ? 'true' : '' }),
    [f.q, f.campaignId, f.source, f.medium, f.status, showArchived]
  );

  const list = data?.data || [];
  const filtered = Object.values(f).some(Boolean);

  const act = async (e, fn, message) => {
    e.stopPropagation();
    try {
      await fn();
      toast(message);
      changed();
    } catch (err) {
      toast(err.message);
    }
  };

  const archive = (e, row) => {
    e.stopPropagation();
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Archive "${row.name || row.short_code}"? The short link stops working for anyone who still has it, `
      + 'and its clicks are kept.')) return;
    return act(e, () => api.urlTracking.links.remove(row.id), 'Tracking URL archived');
  };

  const empty = !loading && !list.length ? (
    <Card>
      <CardBody>
        <EmptyState icon="link" tone="g-cyan" title="No tracking URLs found">
          <p>
            {filtered
              ? 'Nothing matches that filter.'
              : 'A tracking URL is a short link that records every click and then sends the visitor on.'}
          </p>
          {can('utm-links', 'create') && !filtered ? (
            <button type="button" className="btn primary" onClick={() => navigate('/url-tracking/create')}>
              <Icon name="plus" />Create tracking URL
            </button>
          ) : null}
        </EmptyState>
      </CardBody>
    </Card>
  ) : null;

  return (
    <>
      <div className="utfilter">
        <div className="f grow">
          <label htmlFor="utl-q">Search</label>
          <input
            id="utl-q"
            value={f.q}
            placeholder="URL name, destination or campaign…"
            onChange={e => set('q', e.target.value)}
          />
        </div>
        <div className="f">
          <label htmlFor="utl-camp">Campaign</label>
          <select id="utl-camp" value={f.campaignId} onChange={e => set('campaignId', e.target.value)}>
            <option value="">All campaigns</option>
            {(campaigns.data?.data || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="f">
          <label htmlFor="utl-source">Source</label>
          <PresetInput id="utl-source" value={f.source} onChange={v => set('source', v)} options={SOURCES} placeholder="Any" />
        </div>
        <div className="f">
          <label htmlFor="utl-medium">Medium</label>
          <PresetInput id="utl-medium" value={f.medium} onChange={v => set('medium', v)} options={MEDIUMS} placeholder="Any" />
        </div>
        <div className="f">
          <label htmlFor="utl-status">Status</label>
          <select id="utl-status" value={f.status} onChange={e => set('status', e.target.value)}>
            <option value="">Any status</option>
            {LINK_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button
          type="button"
          className={`btn${showArchived ? ' on' : ''}`}
          onClick={() => setShowArchived(v => !v)}
        >
          <Icon name={showArchived ? 'eye' : 'eyeoff'} />Archived
        </button>
      </div>

      <TrackState loading={loading && !data} error={error} empty={empty}>
        <Card>
          <CardHead icon="link" tone="g-cyan" title="Tracking URLs" count={list.length}>
            <button
              type="button"
              className="btn"
              onClick={async () => {
                try {
                  download(await api.urlTracking.analytics.exportLinks(), 'tracking-urls.csv');
                  toast('Tracking URLs exported');
                } catch (err) {
                  toast(err.message === 'Forbidden' ? 'Your role cannot export' : err.message);
                }
              }}
            >
              <Icon name="down" />Export CSV
            </button>
            {can('utm-links', 'create') ? (
              <button type="button" className="btn primary" onClick={() => navigate('/url-tracking/create')}>
                <Icon name="plus" />Create URL
              </button>
            ) : null}
          </CardHead>

          <div className="tscroll">
            <table>
              <thead>
                <tr>
                  <th>URL name</th><th>Campaign</th><th>Recipient</th><th>Source</th><th>Medium</th><th>Content</th>
                  <th>Destination</th><th>Short URL</th><th>Clicks</th><th>Unique</th>
                  <th>Status</th><th>Created</th><th />
                </tr>
              </thead>
              <tbody>
                {list.map(l => {
                  const created = parseDate(l.created_at);
                  return (
                    <tr key={l.id} onClick={() => setSheet({ kind: 'trackLinkView', id: l.id })}>
                      <td>
                        <div className="cname">
                          <Avatar name={l.name || l.short_code} />
                          <div>
                            <b>{l.name || <span className="dim">Untitled</span>}</b>
                            <small>{l.utm_campaign}</small>
                          </div>
                        </div>
                      </td>
                      <td>{l.campaign_name}</td>
                      <td>{l.client_name ? <Pill tone="p-blue" icon="users">{l.client_name}</Pill> : <span className="dim">—</span>}</td>
                      <td><SourcePill value={l.utm_source} /></td>
                      <td className="dim">{l.utm_medium || '—'}</td>
                      <td className="dim">{l.utm_content || '—'}</td>
                      <td><Destination url={l.destination_url} /></td>
                      <td onClick={e => e.stopPropagation()}><ShortUrl url={l.short_url} compact /></td>
                      <td><b>{num(l.clicks)}</b></td>
                      <td className="dim">{num(l.uniqueVisitors)}</td>
                      <td><TrackStatus value={l.status} /></td>
                      <td>
                        {created
                          ? <span className="datep"><Icon name="cal" />{formatDate(created, false, S.dateFmt)}</span>
                          : <Dash />}
                      </td>
                      <td>
                        <div className="acts">
                          <button
                            type="button"
                            title="Analytics for this URL"
                            onClick={e => { e.stopPropagation(); setSheet({ kind: 'trackLinkView', id: l.id }); }}
                          >
                            <Icon name="chart" />
                          </button>

                          {can('utm-links', 'edit') && l.status !== 'Archived' ? (
                            l.status === 'Active' ? (
                              <button
                                type="button"
                                title="Pause — the link stops redirecting"
                                onClick={e => act(e, () => api.urlTracking.links.pause(l.id), 'Tracking URL paused')}
                              >
                                <Icon name="wait" />
                              </button>
                            ) : (
                              <button
                                type="button"
                                title="Activate"
                                onClick={e => act(e, () => api.urlTracking.links.activate(l.id), 'Tracking URL active')}
                              >
                                <Icon name="check" />
                              </button>
                            )
                          ) : null}

                          {can('utm-links', 'edit') ? (
                            <button
                              type="button"
                              title="Edit"
                              onClick={e => { e.stopPropagation(); setSheet({ kind: 'trackLink', id: l.id }); }}
                            >
                              <Icon name="edit" />
                            </button>
                          ) : null}

                          {can('utm-links', 'delete') && l.status !== 'Archived' ? (
                            <button type="button" title="Archive" onClick={e => archive(e, l)}>
                              <Icon name="trash" />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </TrackState>
    </>
  );
}
